import { motion } from 'motion/react';
import { useEffect } from 'react';

import { useNotification } from '@hooks/useNotification';
import { useSettings } from '@hooks/useSettings';
import { orderService } from '@services/orderService';
import { brandColors } from '@utils/color-utils';

/**
 * Hook that listens to order service events and triggers notifications
 * This runs once per app and automatically shows notifications for:
 * - New orders created
 * - Orders filled
 * - Orders cancelled
 */
export function useOrderNotifications() {
  const { showNotification } = useNotification();
  const { advancedMode } = useSettings();

  useEffect(() => {
    const formatNumber = (num: number) => {
      return num.toLocaleString(undefined, {
        maximumFractionDigits: 2,
      });
    };

    // Subscribe to order created events
    const unsubscribeCreated = orderService.subscribeToEvent(
      'orderCreated',
      ({ order }) => {
        const orderTypeText = order.orderType === 'MARKET' ? 'Market' : 'Limit';
        const directionText = order.type === 'CALL' ? 'ABOVE' : 'BELOW';

        const message =
          order.side === 'BUY'
            ? `${orderTypeText} buy order created for ${formatNumber(order.quantity)} ${directionText}`
            : `${orderTypeText} sell order created for ${formatNumber(order.quantity)} ${directionText}`;

        showNotification({
          message,
          type: 'success',
          position: 'below-header',
          duration: 3000,
          metadata: {
            tradeType: order.type,
          },
        });
      },
    );

    // Subscribe to order cancelled events
    const unsubscribeCancelled = orderService.subscribeToEvent(
      'orderCancelled',
      ({ order }) => {
        const orderTypeText = order.orderType === 'MARKET' ? 'Market' : 'Limit';
        const directionText = order.type === 'CALL' ? 'ABOVE' : 'BELOW';

        const message =
          order.side === 'BUY'
            ? `${orderTypeText} buy order cancelled for ${formatNumber(order.quantity)} ${directionText}`
            : `${orderTypeText} sell order cancelled for ${formatNumber(order.quantity)} ${directionText}`;

        showNotification({
          message,
          type: 'info',
          position: 'below-header',
          duration: 3000,
          metadata: {
            tradeType: order.type,
          },
        });
      },
    );

    // Subscribe to order filled events
    const unsubscribeFilled = orderService.subscribeToEvent(
      'orderFilled',
      ({ order, fill }) => {
        if (!fill) {
          return;
        }

        const tradeType = order.type;
        const directionText = tradeType === 'CALL' ? 'ABOVE' : 'BELOW';
        const quantity = order.quantity;
        const side = order.side;
        const strikePrice = order.strikePrice;
        const fillPrice = fill.fillPrice;
        const totalCost = fillPrice * quantity;

        const actionText = side === 'BUY' ? 'Bought' : 'Sold';
        const accentColor =
          tradeType === 'CALL' ? brandColors.green : brandColors.red;

        showNotification({
          message: (
            <div className="flex flex-col gap-1" style={{ color: accentColor }}>
              <div className="text-base font-semibold">
                Order filled for ${formatNumber(totalCost)}
              </div>
              <div className="text-sm opacity-80">
                {actionText} {formatNumber(quantity)} {directionText} at $
                {formatNumber(strikePrice)}
              </div>
            </div>
          ),
          type: 'confetti',
          position: 'below-header',
          duration: 0, // Don't auto-dismiss - user must manually close
          leftContent: (
            <motion.div
              animate={{
                scale: [1, 1.1, 1],
                rotate: [0, -5, 5, 0],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            >
              <img
                width="60"
                height="60"
                src="/happy-froggo.png"
                alt="Success"
                className="w-[60px] h-[60px]"
              />
            </motion.div>
          ),
          metadata: {
            tradeType,
          },
        });
      },
    );

    return () => {
      unsubscribeCreated();
      unsubscribeCancelled();
      unsubscribeFilled();
    };
  }, [showNotification, advancedMode]);
}
